import Highcharts from 'highcharts';
import tinyColor from 'tinycolor2';

import { ChartColors } from 'theme/tokens';
import { InsightsChartSpec } from 'services/streamInsightsQuery';

import { baseInsightsOptions, formatChartValue, tooltipHtml } from './shared';

// Node id prefixes keep the first dimension (sources) and the second (targets)
// distinct even when they share a member label — otherwise a name present in
// both would collapse into one node and could create a cycle. Same convention
// the saved sankey gadget uses.
const FROM_PREFIX = 'a::';
const TO_PREFIX = 'b::';

// heatmap | sankey — one metric across TWO categorical dimensions. The spec
// carries the grid in the matrix shape: `categories` are the second dimension's
// members (x axis / targets) and each entry in `series` is a member of the first
// (y axis / sources), with `data` aligned to `categories`.
const buildCrosstabOptions = (spec: InsightsChartSpec): Highcharts.Options => {
  const isSankey = spec.chartType === 'sankey';
  const { formatting } = spec;
  const rowNames = spec.series.map((s) => s.name);

  const base = baseInsightsOptions({
    spec,
    chartType: spec.chartType,
    usePointName: false,
    showLegend: false,
  });

  if (isSankey) {
    // One link per positive cell. A null cell is a pair with no data, and
    // non-positive weights can't be drawn — the backend already rejects a grid
    // with no positive cell at all.
    const data: { from: string; to: string; weight: number }[] =
      spec.series.flatMap((row, rowIndex) =>
        row.data.flatMap((weight, columnIndex) =>
          weight != null && weight > 0
            ? [
                {
                  from: `${FROM_PREFIX}${rowNames[rowIndex]}`,
                  to: `${TO_PREFIX}${spec.categories[columnIndex] ?? ''}`,
                  weight,
                },
              ]
            : [],
        ),
      );
    const nodes = [
      ...rowNames.map((name) => ({ id: `${FROM_PREFIX}${name}`, name })),
      ...spec.categories.map((name) => ({ id: `${TO_PREFIX}${name}`, name })),
    ];

    return {
      ...base,
      series: [
        {
          type: 'sankey',
          name: spec.title,
          keys: ['from', 'to', 'weight'],
          data,
          nodes,
        },
      ] as unknown as Highcharts.SeriesOptionsType[],
      tooltip: {
        ...base.tooltip,
        formatter(this: Highcharts.TooltipFormatterContextObject): string {
          const point = this.point as Highcharts.Point & {
            from?: string;
            to?: string;
            weight?: number;
            isNode?: boolean;
          };
          if (point.isNode) {
            return tooltipHtml(`<b>${point.name}</b>`);
          }
          const value = formatChartValue(Number(point.weight ?? 0), formatting);
          const from = String(point.from ?? '').replace(FROM_PREFIX, '');
          const to = String(point.to ?? '').replace(TO_PREFIX, '');
          return tooltipHtml(`<b>${from} → ${to}</b>: ${value}`);
        },
      },
      // Sankey has no axes, but leaving them off entirely makes Highcharts warn
      // (#12) — declare them hidden instead, as the saved gadget does.
      xAxis: { visible: false },
      yAxis: { visible: false },
    };
  }

  // Heatmap: one [columnIndex, rowIndex, value] triple per cell. A null value
  // stays null — Highcharts renders it as an empty (nullColor) cell, which is
  // the honest reading of a combination with no data.
  const data = spec.series.flatMap((row, rowIndex) =>
    row.data.map((value, columnIndex) => [columnIndex, rowIndex, value]),
  );

  return {
    ...base,
    series: [
      { type: 'heatmap', name: spec.title, data, borderWidth: 1 },
    ] as unknown as Highcharts.SeriesOptionsType[],
    // Intensity ramp built from the theme palette rather than hardcoded colours,
    // matching the saved heatmap gadget.
    colorAxis: {
      min: 0,
      minColor: tinyColor(ChartColors[0]).lighten(35).toHexString(),
      maxColor: ChartColors[0],
    },
    tooltip: {
      ...base.tooltip,
      formatter(this: Highcharts.TooltipFormatterContextObject): string {
        const point = this.point as Highcharts.Point & {
          value?: number | null;
        };
        const row = rowNames[point.y ?? 0] ?? '';
        const column = spec.categories[point.x ?? 0] ?? '';
        if (point.value == null) {
          return tooltipHtml(`<b>${row} / ${column}</b>: no data`);
        }
        const value = formatChartValue(Number(point.value), formatting);
        return tooltipHtml(`<b>${row} / ${column}</b>: ${value}`);
      },
    },
    xAxis: {
      categories: spec.categories,
      ...(spec.categoryLabel ? { title: { text: spec.categoryLabel } } : {}),
    },
    yAxis: {
      categories: rowNames,
      ...(spec.seriesLabel ? { title: { text: spec.seriesLabel } } : {}),
      // Highcharts reverses a categorical y-axis by default; keep the first
      // series at the top so the chart reads in the order the data arrived.
      reversed: true,
    },
  };
};

export default buildCrosstabOptions;
